import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { appData } from "@stores/bookdata";
import { useAtomValue, useSetAtom } from "jotai";
import { useParams, useNavigate } from "react-router-dom";
import playSound from "@/sound";
import {
  soundPopState,
  wordPopState,
  activityPopState,
  bookSoundPlayerState,
  gamePopState,
} from "@stores/popup";
import { videoState, currentPageState } from "@stores/vars";
import PopupCloseButton from "@components/elements/PopupCloseButton";
import BookVideoWrapContainer from "./BookVideoWrapContainer";

const Wrap = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  background-color: #3e3a62;
  &:not(.first) {
    #bookCover {
      transition: opacity 0.3s;
      opacity: 1;
    }
  }
  &.first {
    .book-sound-button {
      > button {
        left: 50%;
        margin-left: -47px;
      }
    }
    .page-l {
      display: none;
    }
    .page-r {
      opacity: 1;
      background: none;
      width: 833px;
      height: 830px;
      left: 542px;
      top: 185px;
      transform: none;
    }
  }

  &.last {
    .page-r {
      display: none;
    }
    .last-page-label {
      display: block;
      animation: fadeout 0.3s;
      animation-fill-mode: forwards;
      animation-delay: 2s;
    }
  }
`;

const ButtonWrap = styled.div`
  position: absolute;
  right: 80px;
  top: 28px;
  transition: transform 0.8s cubic-bezier(0.65, 0, 0.35, 1);
  transform: translateY(-200px);
  &.hidden {
    display: none;
  }
`;

const IconButton = styled.button`
  display: inline-block;
  width: 212px;
  height: 100px;
  margin-top: 14px;
  &.disabled {
    pointer-events: none;
    // filter: grayscale(1);
    display: none;
  }
  &.ty1 {
    background: url(${require("@assets/img/icon_button_song.png")}) no-repeat;
    background-size: 100% auto;
    margin-right: 14px;
  }
  &.ty2 {
    background: url(${require("@assets/img/icon_button_word.png")}) no-repeat;
    background-size: 100% auto;
    margin-right: 14px;
  }
  &.ty3 {
    background: url(${require("@assets/img/icon_button_activity.png")})
      no-repeat;
    background-size: 100% auto;
    margin-right: 14px;
  }
  &.ty4 {
    background: url(${require("@assets/img/icon_button_game.png")}) no-repeat;
    background-size: 100% auto;
  }
`;

const TitleBox = styled.div`
  position: absolute;
  left: 180px;
  top: 28px;
  display: flex;
  align-items: center;
  animation: slideDown 0.8s cubic-bezier(0.65, 0, 0.35, 1);
  animation-delay: 0.5;
`;
const TitleCoverImage = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 15px;
  background-color: #fff;
  box-sizing: border-box;
  border: 10px solid var(--theme-color);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  > img {
    width: 110%;
    border-radius: 20px;
  }
`;
const CategoyGradeText = styled.div`
  font-size: 26px;
  margin-left: 23px;
`;
const TitleText = styled.div`
  font-size: 40px;
  margin-left: 23px;
  margin-top: 5px;
`;

const LastPageLabel = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: 200px;
  width: 329px;
  height: 97px;
  background: url(${require("@assets/img/img_lastpage.png")}) no-repeat;
  background-size: 100% 100%;
  z-index: 5;
  display: none;
`;

const BookContainer = () => {
  const [curType, setCurType] = useState("book");
  const setSoundAndReadData = useSetAtom(soundPopState);
  const setActivity = useSetAtom(activityPopState);
  const setWord = useSetAtom(wordPopState);
  const setGame = useSetAtom(gamePopState);
  const setBookSoundButton = useSetAtom(bookSoundPlayerState);
  const setCurrentPage = useSetAtom(currentPageState);
  const setPlay = useSetAtom(videoState);
  const data = useAtomValue(appData);

  const params = useParams();
  const navigate = useNavigate();

  const bookData = data.studybooks
    .filter((d) => d.categoryKey === params.categoryKey)[0]
    .books.filter((a) => params.book === a.key)[0];

  const onClose = () => {
    navigate(`/`);
  };

  const onClickSong = () => {
    setPlay(false);
    setBookSoundButton({
      open: false,
      url: "",
    });
    setSoundAndReadData({
      open: true,
      cover: bookData.cover,
      category: bookData.category,
      title: bookData.title,
      grade: bookData.grade,
      url: bookData.song.url,
      songTitle: bookData.song.title,
      caption: bookData.song.caption,
    });
  };

  const onClickWord = () => {
    setPlay(false);
    setBookSoundButton({
      open: false,
      url: "",
    });
    if (bookData.word.length === 0) {
      setWord({
        open: true,
        mode: "word2",
        data: bookData.word || [],
        data2: bookData.idiom || [],
      });
    } else {
      setWord({
        open: true,
        mode: "word",
        data: bookData.word || [],
        data2: bookData.idiom || [],
      });
    }
  };

  const onClickActivity = () => {
    setPlay(false);
    setBookSoundButton({
      open: false,
      url: "",
    });
    setActivity({
      open: true,
      type: bookData.activity.type,
      questions: bookData.activity.questions,
    });
  };

  const onSwap = (value) => {
    setCurType(value);
    if (value === "book") {
      playSound.bgm(require("@assets/sounds/bgm/book.mp3"));
    } else {
      playSound.bgm(null);
    }
  };

  const onClickGame = () => {
    setPlay(false);
    setBookSoundButton({
      open: false,
      url: "",
    });

    setGame({
      open: true,
      data: bookData.game,
      categoryKey: bookData.categoryKey,
    });
  };

  const renderHeaderTitle = useMemo(() => {
    return (
      <TitleBox>
        <TitleCoverImage>
          <img src={bookData.cover} alt="" />
        </TitleCoverImage>
        <div>
          <CategoyGradeText>{`${bookData.category} ${bookData.grade}`}</CategoyGradeText>
          <TitleText>{bookData.title}</TitleText>
        </div>
      </TitleBox>
    );
  }, [bookData]);

  const renderHeaderButtons = useMemo(() => {
    return (
      <ButtonWrap className="activity-buttons">
        <IconButton
          className={
            bookData.song && bookData.song.url !== "" ? "ty1" : "ty1 disabled"
          }
          onClick={onClickSong}
        />
        <IconButton
          className={
            bookData.word.length > 0 || bookData.idiom.length > 0
              ? "ty2"
              : "ty2 disabled"
          }
          onClick={onClickWord}
        />
        <IconButton
          className={
            bookData.activity.questions.length > 0 ? "ty3" : "ty3 disabled"
          }
          onClick={onClickActivity}
        />
        <IconButton
          className={
            bookData.game && bookData.game.resources.length
              ? "ty4"
              : "ty4 disabled"
          }
          onClick={onClickGame}
        />
      </ButtonWrap>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    window.__saveCurPage = undefined;
    setCurrentPage("book");
    document.body.style.setProperty("--theme-color", bookData.color);
    playSound.bgm(require("@assets/sounds/bgm/book.mp3"));
    document.querySelector(
      ".activity-buttons"
    ).style.transform = `translateX(0)`;

    return () => {
      window.__saveCurPage = undefined;
      playSound.bgm(null);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Wrap id="bookContainer" className="first">
      {renderHeaderTitle}
      {renderHeaderButtons}
      <PopupCloseButton onClick={onClose} />
      <BookVideoWrapContainer data={bookData} type={curType} onSwap={onSwap} />
      <LastPageLabel className="last-page-label" />
    </Wrap>
  );
};
export default React.memo(BookContainer);
